//----------------------------------------------------------------------------------------------------------------------
// Vector / Quaternion Conversion Utilities
//----------------------------------------------------------------------------------------------------------------------

import { Matrix, Quaternion, type TransformNode, Vector3 } from '@babylonjs/core';

import type { QuatConfig, Vec3Config } from '../interfaces/level.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Convert a BabylonJS Vector3 to a plain { x, y, z } object.
 */
export function toVec3Object(vector : Vector3) : Vec3Config
{
    return { x: vector.x, y: vector.y, z: vector.z };
}

/**
 * Convert a plain { x, y, z } object to a BabylonJS Vector3.
 */
export function toVector3(vec : Vec3Config) : Vector3
{
    return new Vector3(vec.x, vec.y, vec.z);
}

/**
 * Convert a BabylonJS Quaternion to a plain { x, y, z, w } object.
 */
export function toQuatObject(quat : Quaternion) : QuatConfig
{
    return { x: quat.x, y: quat.y, z: quat.z, w: quat.w }; // eslint-disable-line id-length
}

/**
 * Convert a plain { x, y, z, w } object to a BabylonJS Quaternion.
 */
export function toQuaternion(quat : QuatConfig) : Quaternion
{
    return new Quaternion(quat.x, quat.y, quat.z, quat.w);
}

//----------------------------------------------------------------------------------------------------------------------
// Transform Hierarchy Utilities
//----------------------------------------------------------------------------------------------------------------------

/**
 * Compute a node's transform in canonical Babylon space by walking the parent
 * chain and individually cancelling each import conversion ancestor.
 *
 * For each ancestor, computes its local contribution from BabylonJS's own world
 * matrices. If that local contribution has negative determinant (import conversion
 * node like glTF's __root__), it is individually cancelled from the running matrix
 * via left-multiplication by its inverse.
 *
 * Each conversion node is handled one at a time — no assumptions about hierarchy
 * depth or which node is "the" root. Handles arbitrarily nested import hierarchies.
 */
export function getCanonicalTransform(node : TransformNode) : {
    position : Vector3;
    rotation : Quaternion;
    scaling : Vector3;
}
{
    node.computeWorldMatrix(true);
    let canonical = node.getWorldMatrix().clone();

    // Walk up the parent chain. For each ancestor, compute its local contribution
    // (the difference between its world and its parent's world). If that local
    // has negative determinant, cancel it from the canonical matrix.
    let ancestor = node.parent as TransformNode | null;
    while(ancestor)
    {
        ancestor.computeWorldMatrix(true);
        const ancestorWorld = ancestor.getWorldMatrix();

        // Compute this ancestor's local contribution
        const parentOfAncestor = ancestor.parent as TransformNode | null;
        let ancestorLocal : Matrix;

        if(parentOfAncestor)
        {
            parentOfAncestor.computeWorldMatrix(true);
            ancestorLocal = ancestorWorld.multiply(Matrix.Invert(parentOfAncestor.getWorldMatrix()));
        }
        else
        {
            ancestorLocal = ancestorWorld;
        }

        if(ancestorLocal.determinant() < 0)
        {
            canonical = Matrix.Invert(ancestorLocal).multiply(canonical);
        }

        ancestor = parentOfAncestor;
    }

    const position = new Vector3();
    const rotation = new Quaternion();
    const scaling = new Vector3();
    canonical.decompose(scaling, rotation, position);

    return { position, rotation, scaling };
}

//----------------------------------------------------------------------------------------------------------------------
